import React, { useState, useEffect } from "react";

const colors = {
  bg: "#F8F7F4",
  card: "#FFF",
  border: "#E5E4E1",
  text: "#1A1A1A",
  textSec: "#6B6B6B",
  textTer: "#9B9B9B",
  green: "#16A34A",
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  red: "#DC2626",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
  blue: "#2563EB",
  blueBg: "#EFF6FF",
  amber: "#D97706",
  amberBg: "#FFFBEB",
  amberBorder: "#FDE68A",
};
const fonts = {
  display: "'Source Serif 4', serif",
  body: "'IBM Plex Sans', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

const STATUS_LABELS = {
  draft: "Чернова",
  live: "Активен",
  closed: "Затворен",
  resolved: "Решен",
  final: "Финален",
  voided: "Анулиран",
  pending_review: "Чака преглед",
  rejected: "Отказан",
};
const STATUS_COLORS = {
  live: { bg: colors.greenBg, color: colors.green, border: colors.greenBorder },
  final: { bg: colors.blueBg, color: colors.blue, border: "#BFDBFE" },
  voided: { bg: colors.redBg, color: colors.red, border: colors.redBorder },
  draft: { bg: "#F3F4F6", color: "#6B7280", border: "#D1D5DB" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 4,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        textTransform: "uppercase",
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ── Създаване на пазар ──
function CreateMarketForm({ onCreated }) {
  const [form, setForm] = useState({
    question: "",
    question_en: "",
    description: "",
    category: "",
    end_date: "",
    resolution_source: "",
    initial_probability: 0.5,
    is_hot: false,
    is_trending: false,
    status: "live",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    fetch("/api/categories")
      .then(r => r.json())
      .then(d => {
        const c = d.categories || [];
        setCats(c);
        if (c.length > 0 && !form.category) setForm(prev => ({ ...prev, category: c[0].id }));
      })
      .catch(() => {});
  }, []);

  function update(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch("/api/admin/markets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: "error", text: data.error });
      } else {
        setMessage({
          type: "success",
          text: `Пазарът „${data.market.question.slice(0, 50)}..." е създаден!`,
        });
        setForm({
          question: "",
          question_en: "",
          description: "",
          category: cats[0]?.id || "",
          end_date: "",
          resolution_source: "",
          initial_probability: 0.5,
          is_hot: false,
          is_trending: false,
          status: "live",
        });
        if (onCreated) onCreated();
      }
    } catch {
      setMessage({ type: "error", text: "Мрежова грешка" });
    }
    setLoading(false);
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    outline: "none",
    fontFamily: fonts.body,
    boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textSec,
    display: "block",
    marginBottom: 4,
  };

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: 24,
        marginBottom: 24,
      }}
    >
      <h3 style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
        ➕ Създай нов пазар
      </h3>
      {message && (
        <div
          style={{
            background: message.type === "success" ? colors.greenBg : colors.redBg,
            border: `1px solid ${message.type === "success" ? colors.greenBorder : colors.redBorder}`,
            borderRadius: 8,
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 13,
            color: message.type === "success" ? colors.green : colors.red,
          }}
        >
          {message.text}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Въпрос (BG) *</label>
          <textarea
            value={form.question}
            onChange={e => update("question", e.target.value)}
            required
            rows={2}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Ще влезе ли България в Шенген по суша през 2027?"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Въпрос (EN)</label>
          <input
            type="text"
            value={form.question_en}
            onChange={e => update("question_en", e.target.value)}
            style={inputStyle}
            placeholder="Will Bulgaria join Schengen land borders in 2027?"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Описание / Критерии за решаване *</label>
          <textarea
            value={form.description}
            onChange={e => update("description", e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Решава се ДА ако... Решава се НЕ ако..."
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}
        >
          <div>
            <label style={labelStyle}>Категория *</label>
            <select
              value={form.category}
              onChange={e => update("category", e.target.value)}
              style={inputStyle}
            >
              {cats.map(c => (
                <option key={c.id} value={c.id}>
                  {c.icon} {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Крайна дата *</label>
            <input
              type="date"
              value={form.end_date}
              onChange={e => update("end_date", e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Начална вероятност</label>
            <input
              type="number"
              min="0.01"
              max="0.99"
              step="0.01"
              value={form.initial_probability}
              onChange={e => update("initial_probability", parseFloat(e.target.value))}
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Източник за решаване</label>
          <input
            type="text"
            value={form.resolution_source}
            onChange={e => update("resolution_source", e.target.value)}
            style={inputStyle}
            placeholder="Официално решение на Съвета на ЕС"
          />
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "center" }}>
          <label
            style={{
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.is_trending}
              onChange={e => update("is_trending", e.target.checked)}
            />{" "}
            📈 Тренд
          </label>
          <label
            style={{
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.is_hot}
              onChange={e => update("is_hot", e.target.checked)}
            />{" "}
            🔥 Горещ
          </label>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Статус:
            <select
              value={form.status}
              onChange={e => update("status", e.target.value)}
              style={{ ...inputStyle, width: "auto", padding: "4px 8px" }}
            >
              <option value="live">Активен (публичен)</option>
              <option value="draft">Чернова</option>
            </select>
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "10px 24px",
            fontSize: 14,
            fontWeight: 600,
            color: "#FFF",
            background: colors.green,
            border: "none",
            borderRadius: 8,
            cursor: loading ? "wait" : "pointer",
            fontFamily: fonts.body,
          }}
        >
          {loading ? "Създаване..." : "Създай пазар"}
        </button>
      </form>
    </div>
  );
}

// ── Модал за решаване ──
function ResolveModal({ market, onClose, onResolved }) {
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleResolve() {
    if (outcome === null) {
      setError("Изберете резултат");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/markets/${market.id}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outcome, resolution_note: note, resolution_source_url: sourceUrl }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error);
        setLoading(false);
        return;
      }
      onResolved(data);
      onClose();
    } catch {
      setError("Мрежова грешка");
      setLoading(false);
    }
  }

  async function handleVoid() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/markets/${market.id}/void`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: note || "Анулиран от администратор" }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error);
        setLoading(false);
        return;
      }
      onResolved(data);
      onClose();
    } catch {
      setError("Мрежова грешка");
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (
      !confirm(
        "⚠️ ВНИМАНИЕ: Това ще изтрие пазара ЗАВИНАГИ, включително всички гласове. Сигурни ли сте?"
      )
    )
      return;
    if (!confirm('Потвърдете: Изтриване на "' + market.question.slice(0, 50) + '"?')) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/markets/${market.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error);
        setLoading(false);
        return;
      }
      onResolved(data);
      onClose();
    } catch {
      setError("Мрежова грешка");
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    outline: "none",
    fontFamily: fonts.body,
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.card,
          borderRadius: 12,
          padding: 32,
          width: "100%",
          maxWidth: 500,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ fontFamily: fonts.display, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          Решаване на пазар
        </h3>
        <p style={{ fontSize: 14, color: colors.textSec, marginBottom: 4 }}>{market.question}</p>
        <p style={{ fontSize: 12, color: colors.textTer, marginBottom: 20 }}>
          {market.volume} гласа · {Math.round(market.yes_probability * 100)}% Да
        </p>

        {error && (
          <div
            style={{
              background: colors.redBg,
              border: `1px solid ${colors.redBorder}`,
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 16,
              fontSize: 13,
              color: colors.red,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <button
            onClick={() => setOutcome(1)}
            style={{
              flex: 1,
              padding: "16px",
              borderRadius: 10,
              border: `2px solid ${outcome === 1 ? colors.green : colors.border}`,
              background: outcome === 1 ? colors.greenBg : colors.card,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: outcome === 1 ? colors.green : colors.text,
              }}
            >
              ДА
            </div>
          </button>
          <button
            onClick={() => setOutcome(0)}
            style={{
              flex: 1,
              padding: "16px",
              borderRadius: 10,
              border: `2px solid ${outcome === 0 ? colors.red : colors.border}`,
              background: outcome === 0 ? colors.redBg : colors.card,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 4 }}>❌</div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: outcome === 0 ? colors.red : colors.text,
              }}
            >
              НЕ
            </div>
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSec,
              display: "block",
              marginBottom: 4,
            }}
          >
            URL на източника
          </label>
          <input
            type="url"
            value={sourceUrl}
            onChange={e => setSourceUrl(e.target.value)}
            style={inputStyle}
            placeholder="https://..."
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSec,
              display: "block",
              marginBottom: 4,
            }}
          >
            Бележка (видима за потребителите)
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Потвърдено от официален източник..."
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleResolve}
            disabled={loading || outcome === null}
            style={{
              flex: 1,
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: "#FFF",
              background: outcome === null ? colors.textTer : colors.green,
              border: "none",
              borderRadius: 8,
              cursor: loading || outcome === null ? "not-allowed" : "pointer",
              fontFamily: fonts.body,
            }}
          >
            {loading ? "..." : `Реши като ${outcome === 1 ? "ДА" : outcome === 0 ? "НЕ" : "..."}`}
          </button>
          <button
            onClick={handleVoid}
            disabled={loading}
            style={{
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: colors.amber,
              background: colors.amberBg,
              border: `1px solid ${colors.amberBorder}`,
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            Анулирай
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            style={{
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: colors.red,
              background: colors.redBg,
              border: `1px solid ${colors.redBorder}`,
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            🗑 Изтрий
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "10px 16px",
              fontSize: 14,
              color: colors.textSec,
              background: "transparent",
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            Отказ
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Главен админ панел ──
// ── Admin Settings & Email Tool ──
function AdminSettings() {
  const [tab, setTab] = useState("compose");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [template, setTemplate] = useState("custom");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch("/api/admin/users")
      .then(r => r.json())
      .then(d => {
        setUsers(d.users || []);
        setLoadingUsers(false);
      })
      .catch(() => setLoadingUsers(false));
  }, []);

  const templates = {
    custom: { subject: "", body: "" },
    new_markets: {
      subject: "\u{1F5F3}\uFE0F Нови пазари в BGPoll.org!",
      body: "Здравейте, {{username}}!\n\nДобавихме нови пазари за прогнози в BGPoll.org. Влезте и гласувайте:\n\n[Опишете новите пазари тук]\n\nНе забравяйте \u2014 имате 3 безплатни гласа всеки ден!\n\n\u{1F449} bgpoll.org",
    },
    market_resolved: {
      subject: "\u{1F4CA} Пазар е решен \u2014 проверете резултата!",
      body: "Здравейте, {{username}}!\n\nПазарът [ИМЕ НА ПАЗАРА] беше решен.\n\nРезултат: [ДА/НЕ]\nКолективна прогноза: [XX]%\n\nВижте как се класирахте: bgpoll.org/#leaderboard\n\nПродължавайте да прогнозирате! \u{1F3AF}",
    },
    streak_reminder: {
      subject: "\u{1F525} Не губете серията си в BGPoll!",
      body: "Здравейте, {{username}}!\n\nИмате активна серия от гласуване! Не забравяйте да гласувате днес, за да я запазите.\n\n\u{1F525} 3 дни поред = +5 бонус гласа\n\u{1F525} 7 дни поред = +15 бонус гласа\n\n\u{1F449} bgpoll.org",
    },
    weekly_barometer: {
      subject: "\u{1F4CA} BGPoll Барометър \u2014 седмичен обзор",
      body: "Здравейте, {{username}}!\n\nЕто какво прогнозира колективният разум тази седмица:\n\n[Добавете данни от барометъра]\n\nВижте пълния барометър: bgpoll.org/#barometer\n\nГласувайте и вие! \u{1F5F3}\uFE0F",
    },
    special_offer: {
      subject: "\u{1F381} Специална оферта \u2014 бонус гласове в BGPoll!",
      body: "Здравейте, {{username}}!\n\n[Опишете офертата тук]\n\nНе пропускайте! \u{1F449} bgpoll.org\n\nКупуването на гласове е 100% легално. За разлика от изборите. \u{1F60F}",
    },
  };

  function applyTemplate(key) {
    setTemplate(key);
    if (key !== "custom") {
      setSubject(templates[key].subject);
      setBodyText(templates[key].body);
    }
  }

  function toggleEmail(email) {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  }

  function handleSelectAll() {
    if (selectAll) {
      setSelectedEmails([]);
      setSelectAll(false);
    } else {
      setSelectedEmails(filteredUsers.map(u => u.email));
      setSelectAll(true);
    }
  }

  const filteredUsers = filterText
    ? users.filter(
        u =>
          u.email.includes(filterText.toLowerCase()) ||
          u.username.includes(filterText.toLowerCase())
      )
    : users;

  function buildHtml(bodyRaw, recipientUsername) {
    const body = bodyRaw
      .replace(/\{\{username\}\}/g, recipientUsername || "потребител")
      .replace(/\n/g, "<br>");
    return (
      '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:0;font-family:system-ui,-apple-system,sans-serif;background:#F8F7F4;color:#1A1A1A;"><div style="max-width:560px;margin:0 auto;padding:32px 24px;"><div style="text-align:center;margin-bottom:20px;"><img src="https://bgpoll.org/BGPollBlack.png" alt="BGPoll.org" style="height:80px;margin:0 auto;display:block;" /><p style="font-size:12px;color:#9B9B9B;margin:4px 0 0;">Пазар за прогнози за България</p></div><div style="background:#FFF;border-radius:12px;border:1px solid #E5E4E1;padding:24px;margin-bottom:16px;"><div style="font-size:15px;color:#1A1A1A;line-height:1.7;">' +
      body +
      '</div></div><div style="text-align:center;margin-bottom:16px;"><a href="https://bgpoll.org" style="display:inline-block;background:#16A34A;color:#FFF;font-size:15px;font-weight:700;padding:12px 28px;border-radius:10px;text-decoration:none;">Отвори BGPoll.org \u2192</a></div><div style="text-align:center;font-size:11px;color:#9B9B9B;line-height:1.6;"><p>BGPoll.org е безплатна игра за прогнозиране. Не се залагат истински пари.</p></div></div></body></html>'
    );
  }

  async function handleSend() {
    if (!subject.trim() || !bodyText.trim()) {
      alert("Попълнете тема и текст");
      return;
    }
    if (selectedEmails.length === 0) {
      alert("Изберете поне един получател");
      return;
    }
    if (!confirm("Изпращане на " + selectedEmails.length + " имейла. Продължавате?")) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch("/api/admin/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: selectedEmails,
          subject: subject.trim(),
          html: buildHtml(bodyText, ""),
        }),
      });
      setSendResult(await res.json());
    } catch (e) {
      setSendResult({ error: e.message });
    }
    setSending(false);
  }

  async function handleTestEmail() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch("/api/admin/test-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: testEmail || undefined }),
      });
      setTestResult(await res.json());
    } catch (e) {
      setTestResult({ error: e.message });
    }
    setTesting(false);
  }

  const inputStyle = {
    padding: "8px 12px",
    fontSize: 13,
    border: "1px solid " + colors.border,
    borderRadius: 6,
    outline: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    boxSizing: "border-box",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <button
          onClick={() => setTab("compose")}
          style={{
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 600,
            border: "1px solid " + (tab === "compose" ? colors.text : colors.border),
            borderRadius: 6,
            cursor: "pointer",
            background: tab === "compose" ? colors.text : colors.card,
            color: tab === "compose" ? "#FFF" : colors.textSec,
          }}
        >
          📧 Изпрати имейл
        </button>
        <button
          onClick={() => setTab("test")}
          style={{
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 600,
            border: "1px solid " + (tab === "test" ? colors.text : colors.border),
            borderRadius: 6,
            cursor: "pointer",
            background: tab === "test" ? colors.text : colors.card,
            color: tab === "test" ? "#FFF" : colors.textSec,
          }}
        >
          🧪 Тест
        </button>
      </div>

      {tab === "test" && (
        <div
          style={{
            background: colors.card,
            borderRadius: 10,
            border: "1px solid " + colors.border,
            padding: 20,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
            🧪 Тест на имейл системата
          </h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="Имейл (празно = вашият)"
              style={{ ...inputStyle, width: 280 }}
            />
            <button
              onClick={handleTestEmail}
              disabled={testing}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                border: "none",
                borderRadius: 6,
                background: colors.green,
                color: "#FFF",
                cursor: testing ? "wait" : "pointer",
                opacity: testing ? 0.6 : 1,
              }}
            >
              {testing ? "..." : "Изпрати тест"}
            </button>
          </div>
          {testResult && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: testResult.success ? colors.greenBg : colors.redBg,
                border: "1px solid " + (testResult.success ? colors.greenBorder : colors.redBorder),
                fontSize: 13,
              }}
            >
              {testResult.success ? (
                <div style={{ color: colors.green }}>
                  <strong>✅ Изпратен!</strong> ID: {testResult.resend_response?.id || "—"}
                </div>
              ) : (
                <div style={{ color: colors.red }}>
                  <strong>❌ Грешка</strong>
                  <pre style={{ fontSize: 11, marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "compose" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>
          <div>
            <div
              style={{
                background: colors.card,
                borderRadius: 10,
                border: "1px solid " + colors.border,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div
                style={{ fontSize: 12, fontWeight: 600, color: colors.textSec, marginBottom: 8 }}
              >
                Шаблон:
              </div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {[
                  { id: "custom", label: "✏️ Свободен" },
                  { id: "new_markets", label: "🗳️ Нови пазари" },
                  { id: "market_resolved", label: "📊 Решен пазар" },
                  { id: "streak_reminder", label: "🔥 Серия" },
                  { id: "weekly_barometer", label: "📈 Барометър" },
                  { id: "special_offer", label: "🎁 Оферта" },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t.id)}
                    style={{
                      fontSize: 11,
                      padding: "4px 10px",
                      borderRadius: 4,
                      border: "1px solid " + (template === t.id ? colors.green : colors.border),
                      background: template === t.id ? colors.greenBg : colors.card,
                      color: template === t.id ? colors.green : colors.textSec,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: colors.textSec, marginBottom: 4 }}
              >
                Тема:
              </div>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Тема на имейла..."
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: colors.textSec, marginBottom: 4 }}
              >
                Текст: <span style={{ fontWeight: 400 }}>{"({{username}} за персонализация)"}</span>
              </div>
              <textarea
                value={bodyText}
                onChange={e => setBodyText(e.target.value)}
                rows={10}
                style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.6 }}
                placeholder={"Здравейте, {{username}}!\n\nТекст на имейла..."}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: colors.textSec, marginBottom: 4 }}
              >
                Преглед:
              </div>
              <div
                style={{
                  border: "1px solid " + colors.border,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <iframe
                  srcDoc={buildHtml(bodyText || "Текстът ще се покаже тук...", "потребител")}
                  style={{ width: "100%", height: 280, border: "none" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={handleSend}
                disabled={sending || selectedEmails.length === 0}
                style={{
                  padding: "10px 24px",
                  fontSize: 14,
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                  background: selectedEmails.length > 0 ? colors.green : colors.textTer,
                  color: "#FFF",
                  cursor: sending || selectedEmails.length === 0 ? "not-allowed" : "pointer",
                  opacity: sending ? 0.6 : 1,
                }}
              >
                {sending
                  ? "Изпращане..."
                  : "📧 Изпрати до " +
                    selectedEmails.length +
                    " потребител" +
                    (selectedEmails.length !== 1 ? "я" : "")}
              </button>
              {sendResult && (
                <span
                  style={{
                    fontSize: 13,
                    color: sendResult.error ? colors.red : colors.green,
                    fontWeight: 600,
                  }}
                >
                  {sendResult.error
                    ? "❌ " + sendResult.error
                    : "✅ Изпратени: " + sendResult.sent + ", Грешки: " + sendResult.failed}
                </span>
              )}
            </div>
          </div>

          <div
            style={{
              background: colors.card,
              borderRadius: 10,
              border: "1px solid " + colors.border,
              padding: 12,
              maxHeight: 580,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              Получатели ({selectedEmails.length}/{users.length})
            </div>
            <input
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Търси..."
              style={{ ...inputStyle, width: "100%", marginBottom: 8 }}
            />
            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
              <button
                onClick={handleSelectAll}
                style={{
                  fontSize: 11,
                  padding: "3px 8px",
                  borderRadius: 4,
                  border: "1px solid " + colors.border,
                  background: selectAll ? colors.greenBg : colors.card,
                  color: selectAll ? colors.green : colors.textSec,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {selectAll ? "✓ Всички" : "Избери всички"}
              </button>
              <button
                onClick={() => {
                  setSelectedEmails([]);
                  setSelectAll(false);
                }}
                style={{
                  fontSize: 11,
                  padding: "3px 8px",
                  borderRadius: 4,
                  border: "1px solid " + colors.border,
                  background: colors.card,
                  color: colors.textSec,
                  cursor: "pointer",
                }}
              >
                Изчисти
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {loadingUsers ? (
                <div style={{ padding: 12, color: colors.textTer, fontSize: 12 }}>Зареждане...</div>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => toggleEmail(u.email)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 4px",
                      cursor: "pointer",
                      borderRadius: 4,
                      background: selectedEmails.includes(u.email) ? colors.greenBg : "transparent",
                      fontSize: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        border:
                          "1.5px solid " +
                          (selectedEmails.includes(u.email) ? colors.green : colors.border),
                        background: selectedEmails.includes(u.email) ? colors.green : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FFF",
                        fontSize: 10,
                        flexShrink: 0,
                      }}
                    >
                      {selectedEmails.includes(u.email) && "✓"}
                    </div>
                    <div style={{ overflow: "hidden" }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>@{u.username}</div>
                      <div
                        style={{
                          fontSize: 10,
                          color: colors.textTer,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {u.email}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Users Manager ──
function UsersManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [search, setSearch] = useState("");

  async function loadUsers() {
    setLoading(true);
    const r = await fetch(`/api/admin/users?sort=${sortCol}&order=${sortOrder}`).then(r =>
      r.json()
    );
    setUsers(r.users || []);
    setLoading(false);
  }

  useEffect(() => {
    loadUsers();
  }, [sortCol, sortOrder]);

  function toggleSort(col) {
    if (sortCol === col) {
      setSortOrder(prev => (prev === "DESC" ? "ASC" : "DESC"));
    } else {
      setSortCol(col);
      setSortOrder("DESC");
    }
  }

  const rankIcons = { 1: "🌱", 2: "📊", 3: "🎯", 4: "🔮", 5: "🏆" };
  const rankColors = { 1: "#9B9B9B", 2: "#2563EB", 3: "#7C3AED", 4: "#B45309", 5: "#16A34A" };

  const filtered = search
    ? users.filter(
        u =>
          u.username.includes(search.toLowerCase()) ||
          u.email.includes(search.toLowerCase()) ||
          (u.display_name || "").toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const thStyle = col => ({
    fontSize: 11,
    fontWeight: 600,
    color: sortCol === col ? colors.text : colors.textTer,
    cursor: "pointer",
    padding: "8px 6px",
    textAlign: "left",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    userSelect: "none",
    borderBottom: `2px solid ${sortCol === col ? colors.text : colors.border}`,
  });
  const arrow = col => (sortCol === col ? (sortOrder === "DESC" ? " ▼" : " ▲") : "");

  if (loading) return <div style={{ padding: 20, color: colors.textTer }}>Зареждане...</div>;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.textSec }}>
          {users.length} потребители
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Търси по име, имейл..."
          style={{
            padding: "6px 12px",
            fontSize: 13,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            outline: "none",
            fontFamily: "'IBM Plex Sans', sans-serif",
            width: 240,
          }}
        />
      </div>

      <div
        style={{
          background: colors.card,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: colors.bg }}>
              <th onClick={() => toggleSort("username")} style={thStyle("username")}>
                Потребител{arrow("username")}
              </th>
              <th style={{ ...thStyle(""), cursor: "default" }}>Имейл</th>
              <th onClick={() => toggleSort("total_votes")} style={thStyle("total_votes")}>
                Гласове{arrow("total_votes")}
              </th>
              <th onClick={() => toggleSort("purchased_votes")} style={thStyle("purchased_votes")}>
                Баланс{arrow("purchased_votes")}
              </th>
              <th style={{ ...thStyle(""), cursor: "default" }}>Ранг</th>
              <th onClick={() => toggleSort("accuracy_90d")} style={thStyle("accuracy_90d")}>
                Точност{arrow("accuracy_90d")}
              </th>
              <th style={{ ...thStyle(""), cursor: "default" }}>Серия</th>
              <th onClick={() => toggleSort("created_at")} style={thStyle("created_at")}>
                Регистрация{arrow("created_at")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const rc = rankColors[u.rank_id] || "#9B9B9B";
              const ri = rankIcons[u.rank_id] || "🌱";
              const accuracy = Math.round((u.accuracy_90d || 0) * 100);
              const isOligarch = (u.purchased_votes || 0) >= 500;
              return (
                <tr key={u.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: "8px 6px", fontWeight: 600 }}>
                    @{u.username}
                    {isOligarch && (
                      <span title="Олигарх" style={{ marginLeft: 3 }}>
                        💎
                      </span>
                    )}
                    {u.role === "superadmin" && (
                      <span
                        style={{
                          marginLeft: 4,
                          fontSize: 10,
                          padding: "1px 5px",
                          borderRadius: 3,
                          background: "#7C3AED",
                          color: "#FFF",
                          fontWeight: 600,
                        }}
                      >
                        admin
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 6px", color: colors.textSec, fontSize: 12 }}>
                    {u.email}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontWeight: 600,
                    }}
                  >
                    {u.total_votes}
                  </td>
                  <td style={{ padding: "8px 6px", fontFamily: "'IBM Plex Mono', monospace" }}>
                    <span style={{ color: colors.green, fontWeight: 600 }}>
                      {u.purchased_votes || 0}
                    </span>
                    <span style={{ color: colors.textTer, fontSize: 11 }}>
                      {" "}
                      + {u.free_votes_today}
                    </span>
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: `${rc}15`,
                        color: rc,
                      }}
                    >
                      {ri} {u.rank_name_bg || "Начинаещ"}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontWeight: 600,
                      color:
                        accuracy >= 60 ? colors.green : accuracy > 0 ? colors.text : colors.textTer,
                    }}
                  >
                    {u.total_resolved > 0 ? `${accuracy}%` : "—"}
                    {u.total_resolved > 0 && (
                      <span style={{ fontWeight: 400, color: colors.textTer, fontSize: 11 }}>
                        {" "}
                        ({u.total_resolved})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 6px", textAlign: "center" }}>
                    {(u.vote_streak || 0) > 0 ? (
                      <span style={{ color: "#D97706", fontWeight: 600 }}>🔥{u.vote_streak}</span>
                    ) : (
                      <span style={{ color: colors.textTer }}>—</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontSize: 12,
                      color: colors.textSec,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {new Date(u.created_at).toLocaleDateString("bg-BG", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Category Manager ──
function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newCat, setNewCat] = useState({
    id: "",
    label: "",
    icon: "📌",
    color: "#6B7280",
    bg_color: "#F3F4F6",
    sort_order: 99,
  });
  const [msg, setMsg] = useState(null);

  async function loadCats() {
    const r = await fetch("/api/admin/categories").then(r => r.json());
    setCats(r.categories || []);
    setLoading(false);
  }
  useEffect(() => {
    loadCats();
  }, []);

  async function handleCreate() {
    if (!newCat.id || !newCat.label) {
      setMsg({ type: "error", text: "ID и име са задължителни" });
      return;
    }
    const res = await fetch("/api/admin/categories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newCat),
    });
    const d = await res.json();
    if (!res.ok) {
      setMsg({ type: "error", text: d.error });
      return;
    }
    setMsg({ type: "success", text: "Категорията е създадена" });
    setNewCat({
      id: "",
      label: "",
      icon: "📌",
      color: "#6B7280",
      bg_color: "#F3F4F6",
      sort_order: 99,
    });
    setShowAdd(false);
    loadCats();
  }

  async function handleUpdate(cat) {
    const res = await fetch(`/api/admin/categories/${cat.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cat),
    });
    const d = await res.json();
    if (!res.ok) {
      setMsg({ type: "error", text: d.error });
      return;
    }
    setMsg({ type: "success", text: "Категорията е обновена" });
    setEditing(null);
    loadCats();
  }

  async function handleToggle(cat) {
    await fetch(`/api/admin/categories/${cat.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: cat.is_active ? 0 : 1 }),
    });
    loadCats();
  }

  async function handleDelete(cat) {
    if (
      !confirm(`Изтриване на категория "${cat.label}"? Това е възможно само ако няма пазари в нея.`)
    )
      return;
    const res = await fetch(`/api/admin/categories/${cat.id}`, { method: "DELETE" });
    const d = await res.json();
    if (!res.ok) {
      setMsg({ type: "error", text: d.error });
      return;
    }
    setMsg({ type: "success", text: "Изтрита" });
    loadCats();
  }

  const inputStyle = {
    padding: "8px 10px",
    fontSize: 13,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    outline: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    boxSizing: "border-box",
  };

  if (loading) return <div style={{ padding: 20, color: colors.textTer }}>Зареждане...</div>;

  return (
    <div>
      {msg && (
        <div
          style={{
            background: msg.type === "success" ? colors.greenBg : colors.redBg,
            border: `1px solid ${msg.type === "success" ? colors.greenBorder : colors.redBorder}`,
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 16,
            fontSize: 13,
            color: msg.type === "success" ? colors.green : colors.red,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {msg.text}
          <span onClick={() => setMsg(null)} style={{ cursor: "pointer", fontSize: 16 }}>
            ×
          </span>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <button
          onClick={() => setShowAdd(!showAdd)}
          style={{
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 600,
            color: "#FFF",
            background: showAdd ? colors.textSec : colors.green,
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {showAdd ? "Скрий" : "➕ Нова категория"}
        </button>
      </div>

      {showAdd && (
        <div
          style={{
            background: colors.card,
            borderRadius: 10,
            border: `1px solid ${colors.border}`,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 80px",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>
                ID (латиница, без интервали) *
              </div>
              <input
                value={newCat.id}
                onChange={e =>
                  setNewCat(p => ({
                    ...p,
                    id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                  }))
                }
                style={{ ...inputStyle, width: "100%" }}
                placeholder="crypto"
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>Име *</div>
              <input
                value={newCat.label}
                onChange={e => setNewCat(p => ({ ...p, label: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
                placeholder="Криптовалути"
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>Емоджи</div>
              <input
                value={newCat.icon}
                onChange={e => setNewCat(p => ({ ...p, icon: e.target.value }))}
                style={{ ...inputStyle, width: "100%", textAlign: "center", fontSize: 18 }}
              />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 80px",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>
                Цвят на текста
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="color"
                  value={newCat.color}
                  onChange={e => setNewCat(p => ({ ...p, color: e.target.value }))}
                  style={{ width: 32, height: 32, border: "none", cursor: "pointer" }}
                />
                <input
                  value={newCat.color}
                  onChange={e => setNewCat(p => ({ ...p, color: e.target.value }))}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>Фон цвят</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="color"
                  value={newCat.bg_color}
                  onChange={e => setNewCat(p => ({ ...p, bg_color: e.target.value }))}
                  style={{ width: 32, height: 32, border: "none", cursor: "pointer" }}
                />
                <input
                  value={newCat.bg_color}
                  onChange={e => setNewCat(p => ({ ...p, bg_color: e.target.value }))}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: colors.textTer, marginBottom: 2 }}>Ред</div>
              <input
                type="number"
                value={newCat.sort_order}
                onChange={e =>
                  setNewCat(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))
                }
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={handleCreate}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                color: "#FFF",
                background: colors.green,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Създай
            </button>
            <div style={{ fontSize: 12, color: colors.textTer }}>Преглед:</div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: 4,
                background: newCat.bg_color,
                color: newCat.color,
              }}
            >
              {newCat.icon} {newCat.label || "..."}
            </span>
          </div>
        </div>
      )}

      {/* Category list */}
      <div
        style={{
          background: colors.card,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          overflow: "hidden",
        }}
      >
        {cats.map((cat, i) => {
          const isEdit = editing === cat.id;
          return (
            <div
              key={cat.id}
              style={{
                padding: "12px 16px",
                borderBottom: i < cats.length - 1 ? `1px solid ${colors.border}` : "none",
                opacity: cat.is_active ? 1 : 0.5,
              }}
            >
              {isEdit ? (
                <EditCategoryRow
                  cat={cat}
                  onSave={handleUpdate}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22, width: 32, textAlign: "center" }}>{cat.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{cat.label}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: cat.bg_color,
                          color: cat.color,
                        }}
                      >
                        {cat.id}
                      </span>
                      {!cat.is_active && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "1px 6px",
                            borderRadius: 3,
                            background: colors.redBg,
                            color: colors.red,
                          }}
                        >
                          ДЕАКТИВИРАНА
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textTer, marginTop: 2 }}>
                      Ред: {cat.sort_order}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => setEditing(cat.id)}
                      style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 4,
                        border: `1px solid ${colors.border}`,
                        background: colors.card,
                        cursor: "pointer",
                        color: colors.textSec,
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleToggle(cat)}
                      style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 4,
                        border: `1px solid ${colors.border}`,
                        background: colors.card,
                        cursor: "pointer",
                        color: cat.is_active ? colors.amber : colors.green,
                      }}
                    >
                      {cat.is_active ? "⏸" : "▶️"}
                    </button>
                    <button
                      onClick={() => handleDelete(cat)}
                      style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 4,
                        border: `1px solid ${colors.redBorder}`,
                        background: colors.redBg,
                        cursor: "pointer",
                        color: colors.red,
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditCategoryRow({ cat, onSave, onCancel }) {
  const [form, setForm] = useState({ ...cat });
  const inputStyle = {
    padding: "6px 8px",
    fontSize: 12,
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box",
  };
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "60px 1fr 1fr 60px 60px",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <input
          value={form.icon}
          onChange={e => setForm(p => ({ ...p, icon: e.target.value }))}
          style={{ ...inputStyle, textAlign: "center", fontSize: 18 }}
        />
        <input
          value={form.label}
          onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
          style={inputStyle}
          placeholder="Име"
        />
        <div style={{ display: "flex", gap: 4 }}>
          <input
            type="color"
            value={form.color}
            onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
            style={{ width: 28, height: 28, border: "none", cursor: "pointer" }}
          />
          <input
            type="color"
            value={form.bg_color}
            onChange={e => setForm(p => ({ ...p, bg_color: e.target.value }))}
            style={{ width: 28, height: 28, border: "none", cursor: "pointer" }}
          />
        </div>
        <input
          type="number"
          value={form.sort_order}
          onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
          style={inputStyle}
        />
        <div style={{ display: "flex", gap: 4 }}>
          <button
            onClick={() => onSave(form)}
            style={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 4,
              border: "none",
              background: colors.green,
              color: "#FFF",
              cursor: "pointer",
            }}
          >
            ✓
          </button>
          <button
            onClick={onCancel}
            style={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 4,
              border: `1px solid ${colors.border}`,
              background: colors.card,
              cursor: "pointer",
              color: colors.textSec,
            }}
          >
            ✗
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: colors.textTer }}>
        Преглед:{" "}
        <span
          style={{
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 4,
            background: form.bg_color,
            color: form.color,
          }}
        >
          {form.icon} {form.label}
        </span>
      </div>
    </div>
  );
}

export default function AdminPanel({ user, onBack }) {
  const [stats, setStats] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [resolveMarket, setResolveMarket] = useState(null);
  const [message, setMessage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState("markets"); // "markets" | "categories"

  async function loadData() {
    try {
      const [statsRes, marketsRes] = await Promise.all([
        fetch("/api/admin/stats").then(r => r.json()),
        fetch("/api/admin/markets").then(r => r.json()),
      ]);
      setStats(statsRes.stats);
      setMarkets(marketsRes.markets || []);
    } catch {
      setMessage({ type: "error", text: "Грешка при зареждане" });
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const filtered =
    filter === "all"
      ? markets
      : filter === "overdue"
        ? markets.filter(m => m.status === "live" && new Date(m.end_date) < new Date())
        : markets.filter(m => m.status === filter);

  return (
    <div
      style={{
        fontFamily: fonts.body,
        background: colors.bg,
        minHeight: "100vh",
        color: colors.text,
      }}
    >
      {resolveMarket && (
        <ResolveModal
          market={resolveMarket}
          onClose={() => setResolveMarket(null)}
          onResolved={() => {
            loadData();
            setMessage({ type: "success", text: "Пазарът е решен успешно!" });
          }}
        />
      )}

      {/* Навигация */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(248,247,244,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={onBack}
              style={{
                fontSize: 13,
                padding: "6px 12px",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                background: colors.card,
                cursor: "pointer",
                fontFamily: fonts.body,
                color: colors.textSec,
              }}
            >
              ← Назад
            </button>
            <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 18 }}>
              🔧 Админ панел
            </span>
          </div>
          <div style={{ fontSize: 13, color: colors.textSec }}>
            @{user.username} · {user.role}
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 60px" }}>
        {message && (
          <div
            style={{
              background: message.type === "success" ? colors.greenBg : colors.redBg,
              border: `1px solid ${message.type === "success" ? colors.greenBorder : colors.redBorder}`,
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 20,
              fontSize: 14,
              color: message.type === "success" ? colors.green : colors.red,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {message.text}
            <span onClick={() => setMessage(null)} style={{ cursor: "pointer", opacity: 0.6 }}>
              ✕
            </span>
          </div>
        )}

        {/* Статистики */}
        {stats && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 12,
              marginBottom: 24,
            }}
          >
            {[
              { label: "Активни", value: stats.live_markets, color: colors.green },
              { label: "Чакащи преглед", value: stats.pending_review, color: colors.amber },
              { label: "За решаване", value: stats.overdue_resolution, color: colors.red },
              { label: "Решени", value: stats.resolved_markets, color: colors.blue },
              { label: "Потребители", value: stats.total_users, color: colors.text },
              { label: "Гласове", value: stats.total_votes, color: colors.text },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  background: colors.card,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  padding: "16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 28,
                    fontWeight: 700,
                    color: s.color,
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textTer,
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setActiveTab("markets")}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${activeTab === "markets" ? colors.text : colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              background: activeTab === "markets" ? colors.text : colors.card,
              color: activeTab === "markets" ? "#FFF" : colors.textSec,
              fontFamily: fonts.body,
            }}
          >
            📋 Пазари
          </button>
          <button
            onClick={() => setActiveTab("categories")}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${activeTab === "categories" ? colors.text : colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              background: activeTab === "categories" ? colors.text : colors.card,
              color: activeTab === "categories" ? "#FFF" : colors.textSec,
              fontFamily: fonts.body,
            }}
          >
            🏷️ Категории
          </button>
          <button
            onClick={() => setActiveTab("users")}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${activeTab === "users" ? colors.text : colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              background: activeTab === "users" ? colors.text : colors.card,
              color: activeTab === "users" ? "#FFF" : colors.textSec,
              fontFamily: fonts.body,
            }}
          >
            👥 Потребители
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${activeTab === "settings" ? colors.text : colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              background: activeTab === "settings" ? colors.text : colors.card,
              color: activeTab === "settings" ? "#FFF" : colors.textSec,
              fontFamily: fonts.body,
            }}
          >
            ⚙️ Настройки
          </button>
        </div>

        {activeTab === "categories" && <CategoryManager />}
        {activeTab === "users" && <UsersManager />}
        {activeTab === "settings" && <AdminSettings />}

        {activeTab === "markets" && (
          <>
            {/* Бутон създаване */}
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={() => setShowCreate(!showCreate)}
                style={{
                  padding: "10px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#FFF",
                  background: showCreate ? colors.textSec : colors.green,
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                {showCreate ? "Скрий формата" : "➕ Създай нов пазар"}
              </button>
            </div>

            {showCreate && <CreateMarketForm onCreated={loadData} />}

            {/* Филтри */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { id: "all", label: "Всички" },
                { id: "live", label: "Активни" },
                { id: "overdue", label: "🔴 За решаване" },
                { id: "draft", label: "Чернови" },
                { id: "final", label: "Решени" },
                { id: "voided", label: "Анулирани" },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "6px 14px",
                    border: `1px solid ${filter === f.id ? colors.text : colors.border}`,
                    borderRadius: 20,
                    cursor: "pointer",
                    background: filter === f.id ? colors.text : colors.card,
                    color: filter === f.id ? "#FFF" : colors.textSec,
                    fontFamily: fonts.body,
                  }}
                >
                  {f.label} (
                  {f.id === "all"
                    ? markets.length
                    : f.id === "overdue"
                      ? markets.filter(
                          m => m.status === "live" && new Date(m.end_date) < new Date()
                        ).length
                      : markets.filter(m => m.status === f.id).length}
                  )
                </button>
              ))}
            </div>

            {/* Списък пазари */}
            <div
              style={{ border: `1px solid ${colors.border}`, borderRadius: 10, overflow: "hidden" }}
            >
              {filtered.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: colors.textTer }}>
                  Няма пазари в тази категория
                </div>
              ) : (
                filtered.map((m, i) => {
                  const isOverdue = m.status === "live" && new Date(m.end_date) < new Date();
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        alignItems: "center",
                        gap: 16,
                        padding: "14px 20px",
                        background: isOverdue ? "#FEF2F2" : colors.card,
                        borderBottom:
                          i < filtered.length - 1 ? `1px solid ${colors.border}` : "none",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.35,
                            marginBottom: 4,
                          }}
                        >
                          {m.question}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: colors.textTer,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <StatusBadge status={m.status} />
                          {isOverdue && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: colors.red }}>
                              ⏰ ПРОСРОЧЕН
                            </span>
                          )}
                          <span>
                            До{" "}
                            {new Date(m.end_date).toLocaleDateString("bg-BG", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                          <span>·</span>
                          <span style={{ fontFamily: fonts.mono }}>{m.volume} гласа</span>
                          <span>·</span>
                          <span style={{ fontFamily: fonts.mono }}>
                            {Math.round(m.yes_probability * 100)}% Да
                          </span>
                          {m.outcome !== null && m.outcome !== undefined && (
                            <span
                              style={{
                                fontWeight: 700,
                                color: m.outcome === 1 ? colors.green : colors.red,
                              }}
                            >
                              → {m.outcome === 1 ? "ДА" : "НЕ"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {m.status === "live" && (
                          <button
                            onClick={() => setResolveMarket(m)}
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              padding: "6px 14px",
                              borderRadius: 6,
                              border: `1px solid ${isOverdue ? colors.red : colors.greenBorder}`,
                              background: isOverdue ? colors.redBg : colors.greenBg,
                              color: isOverdue ? colors.red : colors.green,
                              cursor: "pointer",
                              fontFamily: fonts.body,
                            }}
                          >
                            {isOverdue ? "⚡ Реши" : "Реши"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
